import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, ScrollView, StyleSheet, useWindowDimensions, View, ViewProps } from 'react-native'
import { Button, Color, Font, PopUp, Radius, Spacing, Text } from 'zens-storybook'

import { Panel } from '../layout'
import ChatMessageBubble from './chat-message-bubble'
import ChatInput from './chat-input'
import ChatPlan from './chat-plan'
import ChatList from './chat-list'
import PreviewTable from './preview-table'
import { useChat } from './use-chat'
import { PREVIEW_ROW_LIMIT, PREVIEW_COLUMN_LIMIT } from './constants'

const CHAT_LIST_WIDTH = 400
const COMPACT_CHAT_BREAKPOINT = 1350

const styles = StyleSheet.create({
  aiAssistantContainer: {
    flexDirection: 'column',
    height: '100%'
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.S,
  },
  chatHeaderWithHistory: {
    // Match the chat container's right edge, excluding the history panel and gap.
    marginRight: CHAT_LIST_WIDTH + Spacing.M,
  },
  newChatButton: {
    borderRadius: Radius.M,
    borderColor: Color.calmGrey,
    alignSelf: 'flex-start',
    paddingHorizontal: Spacing.S,
    height: 43
  },
  chatAndHistoryContainer: {
    flex: 1,
    flexDirection: 'row',
    marginBottom: -Spacing.S,
    position: 'relative',
  },
  chatContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-end',
    backgroundColor: Color.simplyWhite,
    marginTop: Spacing.S,
    marginRight: Spacing.M,
    borderRadius: Radius.M,
    paddingTop: Spacing.XS,
    borderColor: Color.calmGrey,
    borderWidth: 1,
  },
  compactChatContainer: {
    marginRight: 0,
  },
  chatArea: {
    flex: 1,
  },
  chatPanel: {
    flex: 1,
  },
  chatContent: {
    flex: 1,
  },
  messagesContainer: {
    flex: 1,
    padding: Spacing.L,
  },
  creditsNotice: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 680,
    marginTop: Spacing.L,
    padding: Spacing.M,
    borderRadius: Radius.L,
    backgroundColor: Color.white,
    borderColor: Color.graphicGrey,
    borderWidth: 1,
  },
  creditsNoticeText: {
    fontFamily: Font.regular,
    fontSize: 14,
    color: Color.metalGrey,
    textAlign: 'center',
  },
  messagesScroll: {
    flex: 1,
  },
  chatListPanel: {
    width: CHAT_LIST_WIDTH,
    backgroundColor: Color.simplyWhite,
    marginTop: Spacing.S,
    borderRadius: Radius.M,
    borderWidth: 1,
    borderColor: Color.calmGrey,
    overflow: 'visible',
  },
  compactChatListBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.18)',
    zIndex: 20,
  },
  compactChatListDrawer: {
    position: 'absolute',
    top: Spacing.S,
    right: 10,
    bottom: 0,
    zIndex: 30,
    paddingBottom: Spacing.S,
  },
  compactChatListPanel: {
    flex: 1,
    height: '100%',
    marginTop: 0,
  },
  compactChatListToggle: {
    position: 'absolute',
    right: Spacing.L,
    zIndex: 40,
  },
  compactChatListToggleContent: {
    width: 56,
    minWidth: 56,
    height: 56,
    borderRadius: 28,
    borderColor: Color.calmGrey,
    backgroundColor: Color.simplyWhite,
  },
  compactChatListToggleContentActive: {
    backgroundColor: Color.sleepingGrey,
  },
  modalContent: {
    width: '60%',
  },
  modalTitle: {
    fontFamily: Font.regular,
    fontSize: 20,
    marginBottom: Spacing.S,
  },
  modalScrollView: {
    maxHeight: 400,
  },
  modalCaption: {
    fontFamily: Font.regular,
    fontSize: 12,
    color: Color.graphiteGrey,
    marginTop: Spacing.XS,
  },
})

interface Props extends ViewProps {
  companyId: number
}

const AssistantChat = (props: Props) => {
  const { companyId, style, ...otherProps } = props
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const [isCompactChatListOpen, setIsCompactChatListOpen] = useState(false)

  const {
    scrollViewRef,
    inputRef,
    messages,
    inputText,
    setInputText,
    isLoading,
    chats,
    currentChatId,
    isLoadingChats,
    isInputFocused,
    setIsInputFocused,
    inputHeight,
    setInputHeight,
    previewModalData,
    setPreviewModalData,
    hoveredPreviewAction,
    setHoveredPreviewAction,
    hoveredMessageAction,
    setHoveredMessageAction,
    copiedMessageId,
    selectedChatIds,
    isDeletingChats,
    hoveredChatTitleId,
    setHoveredChatTitleId,
    copyMessageContent,
    handleReportDownload,
    handleToggleChatSelection,
    handleSelectAllChats,
    handleClearSelection,
    handleBulkDelete,
    handleSelectChat,
    handleNewChat,
    handleKeyPress,
    sendMessage,
    cancelRequest,
    usage,
    usageError,
    isCreditsExhausted,
    refreshUsage,
  } = useChat(companyId)
  const isCompactLayout = width < COMPACT_CHAT_BREAKPOINT
  const compactChatListWidth = Math.min(CHAT_LIST_WIDTH, Math.max(width - Spacing.L * 2, 280))
  const compactChatListToggleBottom = Math.min(Math.max(inputHeight, 40), 120) + Spacing.XL * 2

  useEffect(() => {
    if (!isCompactLayout) {
      setIsCompactChatListOpen(false)
    }
  }, [isCompactLayout])

  const closeCompactChatList = () => setIsCompactChatListOpen(false)
  const toggleCompactChatList = () => setIsCompactChatListOpen((current) => !current)
  const handleCompactChatSelect = (chatId: string) => {
    handleSelectChat(chatId)
    closeCompactChatList()
  }

  return (
    <View style={[styles.aiAssistantContainer, style]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: Spacing.S }}>
        <Button
          contentContainerStyle={styles.newChatButton}
          onPress={handleNewChat}
          light
          title={t('+ New')}
        />
        <ChatPlan usage={usage} error={usageError} onRetry={refreshUsage} />
      </View>
      <View style={[styles.chatAndHistoryContainer, style]} {...otherProps}>
        <View style={[styles.chatContainer, isCompactLayout && styles.compactChatContainer]}>
          <View style={styles.chatArea}>
            <Panel style={styles.chatPanel}>
              <View style={styles.chatContent}>
                <ScrollView ref={scrollViewRef} style={styles.messagesScroll} contentContainerStyle={styles.messagesContainer}>
                  <ChatMessageBubble
                    key="intro"
                    message={{
                      id: 'intro',
                      role: 'assistant',
                      type: 'message',
                      content: t("Hi, I'm your AI Data Assistant. I can help you with:\n" +
                        "- Retrieving occupancy data for buildings, floors, zones and workspaces\n" +
                        "- Creating reports with custom layouts, time ranges and granularity\n" +
                        "- Answering follow-up questions about your results\n\n" +
                        "I cannot draw conclusions, give recommendations, or provide employee information.\n\n" +
                        "For tips, examples and more details, check the full [user guide](/companies/{{companyId}}/assistant/guide).", { companyId }),
                      status: messages.length === 0 ? undefined : 'done',
                      createdAt: new Date(),
                    }}
                    hoveredPreviewAction={hoveredPreviewAction}
                    setHoveredPreviewAction={setHoveredPreviewAction}
                    hoveredMessageAction={hoveredMessageAction}
                    setHoveredMessageAction={setHoveredMessageAction}
                    copiedMessageId={copiedMessageId}
                    onCopyMessage={copyMessageContent}
                    onExpandPreview={setPreviewModalData}
                    onDownloadReport={handleReportDownload}
                  />
                  {messages.map((message) => (
                    <ChatMessageBubble
                      key={message.id}
                      message={message}
                      hoveredPreviewAction={hoveredPreviewAction}
                      setHoveredPreviewAction={setHoveredPreviewAction}
                      hoveredMessageAction={hoveredMessageAction}
                      setHoveredMessageAction={setHoveredMessageAction}
                      copiedMessageId={copiedMessageId}
                      onCopyMessage={copyMessageContent}
                      onExpandPreview={setPreviewModalData}
                      onDownloadReport={handleReportDownload}
                    />
                  ))}
                  {isCreditsExhausted && (
                    <View style={styles.creditsNotice} role="alert" testID="AssistantChat.CreditsNotice">
                      <Text style={styles.creditsNoticeText}>{usage?.plan === 'premium' ? t('Your chatbot credits are exhausted. Please contact the support team for assistance.') : t('No chatbot credits remaining. Upgrade to premium or wait for the credits to reset on the first day of next month (UTC).')}</Text>
                    </View>
                  )}
                </ScrollView>
              </View>
            </Panel>
          </View>
          <ChatInput
            ref={inputRef}
            inputText={inputText}
            isLoading={isLoading}
            disabled={isCreditsExhausted}
            isInputFocused={isInputFocused}
            inputHeight={inputHeight}
            onChangeText={(text) => {
              setInputText(text)
              if (!text) setInputHeight(40)
            }}
            onSend={sendMessage}
            onCancel={cancelRequest}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            onContentSizeChange={setInputHeight}
          />
        </View>
        {isCompactLayout ? (
          <>
            {isCompactChatListOpen && (
              <>
                <Pressable
                  style={styles.compactChatListBackdrop}
                  onPress={closeCompactChatList}
                  accessibilityRole="button"
                  accessibilityLabel={t('Close chats panel')}
                  testID="AssistantChat.ChatListBackdrop"
                />
                <View style={[styles.compactChatListDrawer, { width: compactChatListWidth }]}>
                  <ChatList
                    chats={chats}
                    currentChatId={currentChatId}
                    isLoadingChats={isLoadingChats}
                    selectedChatIds={selectedChatIds}
                    isDeletingChats={isDeletingChats}
                    hoveredChatTitleId={hoveredChatTitleId}
                    setHoveredChatTitleId={setHoveredChatTitleId}
                    onSelectChat={handleCompactChatSelect}
                    onToggleChatSelection={handleToggleChatSelection}
                    onSelectAllChats={handleSelectAllChats}
                    onClearSelection={handleClearSelection}
                    onBulkDelete={handleBulkDelete}
                    onClose={closeCompactChatList}
                    style={styles.compactChatListPanel}
                  />
                </View>
              </>
            )}
            <Button
              left={isCompactChatListOpen ? 'close' : 'menu'}
              light
              round
              onPress={toggleCompactChatList}
              style={[styles.compactChatListToggle, { bottom: compactChatListToggleBottom }]}
              contentContainerStyle={[
                styles.compactChatListToggleContent,
                isCompactChatListOpen && styles.compactChatListToggleContentActive,
              ]}
              accessibilityLabel={isCompactChatListOpen ? t('Close chats panel') : t('Open chats panel')}
              testID="AssistantChat.ChatListToggle"
            />
          </>
        ) : (
          <ChatList
            chats={chats}
            currentChatId={currentChatId}
            isLoadingChats={isLoadingChats}
            selectedChatIds={selectedChatIds}
            isDeletingChats={isDeletingChats}
            hoveredChatTitleId={hoveredChatTitleId}
            setHoveredChatTitleId={setHoveredChatTitleId}
            onSelectChat={handleSelectChat}
            onToggleChatSelection={handleToggleChatSelection}
            onSelectAllChats={handleSelectAllChats}
            onClearSelection={handleClearSelection}
            onBulkDelete={handleBulkDelete}
          />
        )}
      </View>

      <PopUp
        visible={!!previewModalData}
        onClose={() => setPreviewModalData(null)}
        style={styles.modalContent}
      >
        <Text style={styles.modalTitle}>{t('Preview')}</Text>
        {previewModalData && (
          <ScrollView style={styles.modalScrollView} nestedScrollEnabled>
            <PreviewTable data={previewModalData} variant="modal" />
          </ScrollView>
        )}
        {/* <Text style={styles.modalCaption}>
          {t('* Preview is limited to the first {{rows}} rows and {{columns}} columns', { rows: PREVIEW_ROW_LIMIT, columns: PREVIEW_COLUMN_LIMIT })}
        </Text> */}
      </PopUp>
    </View>
  )
}

interface SkeletonProps extends ViewProps {}
AssistantChat.Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { width } = useWindowDimensions()
  const isCompactLayout = width < COMPACT_CHAT_BREAKPOINT

  return (
    <View style={[styles.aiAssistantContainer, style]} {...otherProps}>
      <View style={styles.chatArea}>
        <Panel.Skeleton style={styles.chatPanel} />
      </View>
      {!isCompactLayout && <Panel.Skeleton style={styles.chatListPanel} />}
      {isCompactLayout && (
        <Button.Skeleton
          round
          style={[styles.compactChatListToggle, { bottom: Spacing.XL * 2 }]}
          contentStyle={styles.compactChatListToggleContent}
        />
      )}
    </View>
  )
}

export default AssistantChat
